import React, { useEffect, useRef, useState } from 'react';
import PointDialogModal from './PointDialogModal';

// Unobtrusive bottom-right progress/completion tiles for the per-point
// pipeline agents. Subscribes to the run's SSE channel; every trace event
// carries pointUuid (parallel point slots), so each point gets its own tile:
// a live one while an agent runs, then a clickable "finished" one that opens
// the point dialog scrolled to its freshest message. Run-scoped events
// (stages 0/1, batch) are ignored — the batch panel covers those.

const ROLE_LABEL = {
  writer: 'Writer', critic: 'Critic', factcheck: 'Fact-check', formal: 'Formalizer',
  tres: 'Researcher', taskgen: 'Generator', tfact: 'Task-Checker', tcheck: 'Checklist',
};
const NODE_LABEL = {
  'task-research': 'Researcher', 'task-gen': 'Generator', 'task-fact': 'Task-Checker',
  'task-gate-card': 'Checklist', 'task-redo': 'Card redo', 'critic-loop': 'Writer loop',
  'writer-note': 'Writer note', 'approve-version': 'Approve', regenerate: 'Regenerate',
  '2a': 'Write', '2b': 'Revise', hint_picture: 'Hint picture', hints_batch: 'Hints',
};

// The newest 'turn' of a replayed trace names the stage better than the node.
const lastTurnOf = (trace) => {
  for (let i = (trace || []).length - 1; i >= 0; i--) {
    if (trace[i] && trace[i].t === 'turn') return trace[i];
  }
  return null;
};

const stageText = (role, round) =>
  `${ROLE_LABEL[role] || role || 'Running'}${round != null ? ` · round ${round}` : ''}`;

const PipelineNotifications = ({ questId, runId, locations, onChange }) => {
  // uuid -> { uuid, stage, status: 'running'|'done'|'error', errors, hidden, ts }
  const [items, setItems] = useState({});
  const [openUuid, setOpenUuid] = useState(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const nameOf = (uuid) => {
    const loc = (locations || []).find((l) => l && l.uuid === uuid);
    return (loc && loc.name) || 'Point';
  };

  useEffect(() => {
    if (!runId) return undefined;
    setItems({}); // fresh run — drop tiles of the previous one
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);

    const upsert = (uuid, patch) => setItems((prev) => {
      const cur = prev[uuid] || { uuid, stage: 'Starting…', status: 'running', errors: null, hidden: false, ts: Date.now() };
      return { ...prev, [uuid]: { ...cur, ...patch, ts: Date.now() } };
    });

    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }

      if (msg.type === 'pipeline_replay' && msg.pointUuid) {
        // Restore live tiles after a reload; finished slots stay silent —
        // UNLESS we hold a live tile for them (the agent finished while the
        // connection was down): flip it so it never spins forever.
        if (msg.status === 'running') {
          const turn = lastTurnOf(msg.trace);
          upsert(msg.pointUuid, {
            status: 'running',
            stage: turn ? stageText(turn.role, turn.round) : (NODE_LABEL[msg.node] || 'Running…'),
          });
        } else {
          const cur = itemsRef.current[msg.pointUuid];
          if (cur && cur.status === 'running') {
            if (msg.status === 'stopped') {
              setItems((prev) => { const next = { ...prev }; delete next[msg.pointUuid]; return next; });
            } else {
              upsert(msg.pointUuid, { status: msg.status === 'done' ? 'done' : 'error', hidden: false });
            }
          }
        }
        return;
      }

      if (msg.type !== 'pipeline_trace' || !msg.ev || !msg.ev.pointUuid) return;
      const ev = msg.ev;
      const uuid = ev.pointUuid;

      if (ev.t === 'turn') {
        upsert(uuid, { status: 'running', stage: stageText(ev.role, ev.round) });
      } else if (ev.t === 'status') {
        // First signal of a fresh generation opens the tile, named after the
        // agent (ev.node); later 'turn' markers refine it with role + round.
        const cur = itemsRef.current[uuid];
        if (!cur || cur.status !== 'running') {
          upsert(uuid, { status: 'running', stage: NODE_LABEL[ev.node] || 'Starting…' });
        }
      } else if (ev.t === 'done') {
        const errs = ev.errors || null;
        if (!ev.ok && errs && errs.some((x) => /stopped by user/i.test(String(x)))) {
          // The human pressed Stop — nothing to notify about.
          setItems((prev) => { const next = { ...prev }; delete next[uuid]; return next; });
          return;
        }
        upsert(uuid, { status: ev.ok ? 'done' : 'error', errors: errs, hidden: false });
      }
    };
    es.onerror = () => { /* auto-reconnects; replay restores live tiles */ };
    return () => es.close();
  }, [runId]);

  const dismiss = (uuid) => setItems((prev) => {
    const cur = prev[uuid];
    if (!cur) return prev;
    if (cur.status === 'running') return { ...prev, [uuid]: { ...cur, hidden: true } }; // resurfaces on finish
    const next = { ...prev };
    delete next[uuid];
    return next;
  });

  const openDialog = (uuid) => {
    setOpenUuid(uuid);
    setItems((prev) => {
      const cur = prev[uuid];
      if (!cur || cur.status === 'running') return prev;
      const next = { ...prev };
      delete next[uuid]; // a clicked "finished" tile is consumed
      return next;
    });
  };

  const visible = Object.values(items)
    .filter((it) => !it.hidden)
    .sort((a, b) => a.ts - b.ts)
    .slice(-5); // the freshest five; older finished tiles wait underneath

  if (!visible.length && !openUuid) return null;

  return (
    <>
      <div style={{
        position: 'fixed', right: 16, bottom: 16, zIndex: 900,
        display: 'flex', flexDirection: 'column', gap: 8, width: 280,
      }}>
        {visible.map((it) => (
          <div
            key={it.uuid}
            onClick={() => openDialog(it.uuid)}
            title={it.status === 'error'
              ? `${(it.errors || []).join('; ') || 'Error'} — click to open the dialog`
              : 'Click to open the point dialog'}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              background: 'var(--bg, #fff)', color: 'var(--text, #202124)',
              border: '1px solid var(--border, #ddd)', borderRadius: 8,
              borderLeft: `4px solid ${it.status === 'running' ? '#1a73e8' : it.status === 'done' ? '#188038' : '#c5221f'}`,
              boxShadow: '0 2px 10px rgba(0,0,0,0.18)', padding: '8px 10px', fontSize: 12.5,
              animation: 'plnotif-in 0.18s ease-out',
            }}
          >
            <span style={{ fontSize: 15, flexShrink: 0 }}>
              {it.status === 'running'
                ? <span style={{ display: 'inline-block', width: 13, height: 13, border: '2px solid #1a73e8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'plnotif-spin 0.9s linear infinite' }} />
                : it.status === 'done' ? '✅' : '❌'}
            </span>
            <span style={{ overflow: 'hidden', flex: 1 }}>
              <span style={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(it.uuid)}</span>
              <span style={{ display: 'block', opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {it.status === 'running' ? it.stage : it.status === 'done' ? `${it.stage} — finished` : `${it.stage} — failed`}
              </span>
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); dismiss(it.uuid); }}
              title="Dismiss"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.5, fontSize: 14, padding: 2, flexShrink: 0 }}
            >✕</button>
          </div>
        ))}
        <style>{`
          @keyframes plnotif-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
          @keyframes plnotif-spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>

      {openUuid && (
        <PointDialogModal
          questId={questId}
          uuid={openUuid}
          pointName={nameOf(openUuid)}
          autoScroll
          onClose={() => setOpenUuid(null)}
          onChanged={onChange}
        />
      )}
    </>
  );
};

export default PipelineNotifications;
